import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
// UI

import {
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
} from '@chakra-ui/react'

import DownloadButton from '@/components/Elements/Button/DownloadButton'
import ViewFileButton from '@/components/Elements/Button/ViewFileButton'
import { FaFileExcel, FaFilePdf } from 'react-icons/fa'

// Custom

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { setDocumentDetailModal } from '@/stores/ui/uiSlice'

import EditableTable from '@/components/Table/EditableTable'
import useFile from '@/features/files/useFile'
import useOfferAction from '@/features/offers/hooks/useOfferAction'
import { getFileExtension, readSpecificCellsFromExcel } from '@/utils/fileHelper'
import { createColumnHelper } from '@tanstack/react-table'

const DocumentDetailModal = () => {
  // Redux
  const { show, title, path, fileType, isMultiple, canUpload, canDelete, offerId, showVersion, allowedFileTypes } =
    useSelector((state) => state.ui.documentDetailModal)

  // Local
  const [newFiles, setNewFiles] = useState([])
  const [data, setData] = useState([])

  // React-form
  const { getValues, setValue } = useFormContext()

  // Hooks
  const { useUploadFile } = useFile()
  const { useRemoveOfferFile } = useOfferAction()
  const dispatch = useDispatch()
  const uploadFile = useUploadFile()
  const removeOfferFile = useRemoveOfferFile()

  const columnHelper = createColumnHelper()

  //Translated Variables
  // const fileDetailTableData = useMemo(() =>  documents.map((doc) => ({
  //   version: doc.version,
  //   createdAt: moment(doc.createdAt).format('DD-MMM-YYYY'),
  //   createdBy: doc.createdBy?.fullName || '',
  //   fileId: doc._id,
  //   originalFileName: doc.originalFileName,
  // })), [])

  const transformFilesData = (documents) =>
    documents.map((doc) => ({
      version: doc.version,
      createdAt: moment(doc.createdAt).format('DD-MMM-YYYY'),
      createdBy: doc.createdBy?.fullName || '',
      fileId: doc._id,
      originalFileName: doc.originalFileName,
    }))

  useEffect(() => {
    if (show) {
      const documents = getValues(path)
      setData(transformFilesData(documents))
    }
  }, [getValues, path, show])

  const handleRemoveFile = useCallback(
    async (fileId) => {
      // Try Delete File
      removeOfferFile.mutate(
        { id: offerId, fileType, fileId },
        {
          onSuccess: () => {
            const currentDocuments = getValues(path)

            const newDocuments = currentDocuments.filter((doc) => doc._id !== fileId)
            setValue(path, newDocuments, { shouldDirty: true })
            setData(transformFilesData(newDocuments))
          },
        }
      )
    },
    [fileType, getValues, offerId, path, removeOfferFile, setValue]
  )

  const handleFileChange = async (e) => {
    if (e.target.files.length > 0) {
      setNewFiles(e.target.files)

      const files = e.target.files
      const match = path?.match(/^subSystems\.(\d+)\.internalCostSheet$/)

      if (match) {
        const index = match[1]
        try {
          const cells = await readSpecificCellsFromExcel(files[0], 'Quotation BQ (Core)', ['S675', 'G612'])

          let parsedNum = null
          if (cells['S675'] !== null && cells['S675'] !== '' && !isNaN(Number(cells['S675']))) {
            parsedNum = Number(cells['S675'])
          } else if (cells['G612'] !== null && cells['G612'] !== '' && !isNaN(Number(cells['G612']))) {
            parsedNum = Number(cells['G612'])
          }

          if (parsedNum !== null) {
            setValue(`subSystems.${index}.costSheetContractValue`, parsedNum, { shouldDirty: true })
            const countryOfInstallation = getValues('countryOfInstallation')
            if (countryOfInstallation === 'hk') {
              if (parsedNum >= 5000000) {
                setValue('SOPQuestionAnswers.isOver5Million', true, { shouldDirty: true })
              } else {
                setValue('SOPQuestionAnswers.isOver5Million', false, { shouldDirty: true })
              }
            }
          }
        } catch (error) {
          console.error('Error parsing cost sheet for contract value', error)
        }
      }
    }
  }

  const handleUploadFile = async () => {
    if (fileType && newFiles.length > 0) {
      const newVersion = data.length > 0 ? Math.max(...data.map((doc) => parseInt(doc.version) || 0)) + 1 : 0

      dispatch(showLoadingOverlay())
      uploadFile.mutate(
        { version: newVersion, type: fileType, files: newFiles },
        {
          onSuccess: (res) => {
            const currentDocuments = getValues(path)

            const newDocuments = [...currentDocuments, ...res.data]

            setValue(path, newDocuments, { shouldDirty: true })
            setData(transformFilesData(newDocuments))
            setNewFiles([])
          },
          onSettled: () => {
            dispatch(hideLoadingOverlay())
          },
        }
      )
    }
  }

  const closeModal = () => {
    dispatch(
      setDocumentDetailModal({
        show: false,
        documentType: '',
      })
    )
  }

  const onSubmit = (e) => {
    e.preventDefault()
  }

  const columnDef = useMemo(
    () => [
      columnHelper.accessor('version', {
        header: () => <span>File Version</span>,
      }),
      columnHelper.accessor('createdAt', {
        header: () => <span>Upload Date</span>,
      }),
      columnHelper.accessor('createdBy', {
        header: () => <span>Upload By</span>,
      }),
      columnHelper.display({
        id: 'fileType',
        header: () => <span>File Type</span>,
        cell: ({ row: { original } }) => {
          const fileType = getFileExtension(original.originalFileName)

          return (
            <Flex gap={1} alignItems={'center'} justifyContent={'center'}>
              {fileType === 'pdf' && <FaFilePdf />}
              {fileType.includes('xls') && <FaFileExcel />}
              <span>{fileType}</span>
            </Flex>
          )
        },
      }),
      columnHelper.accessor('originalFileName', {
        header: () => <span>File Name</span>,
        size: 150,
      }),
      columnHelper.accessor('fileId', {
        header: () => <span></span>,
        size: 100,
        cell: ({ getValue, row: { original } }) => (
          <HStack>
            {getFileExtension(original.originalFileName) === 'pdf' && (
              <ViewFileButton fileName={original.originalFileName} fileId={getValue()} />
            )}
            <DownloadButton fileId={getValue()} fileName={original.originalFileName} />

            {canDelete && (
              <Button colorScheme="red" onClick={() => handleRemoveFile(getValue())}>
                Delete
              </Button>
            )}
          </HStack>
        ),
      }),
    ],
    [columnHelper]
  )

  return (
    <Modal isOpen={show} onClose={closeModal}>
      <ModalOverlay />
      <ModalContent maxW="1200px">
        <form onSubmit={onSubmit}>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box w="100%" overflowX={''}>
              <EditableTable
                columnDef={columnDef}
                data={show ? data : []}
                disabled={true}
                showFooter={false}
                tableWidth="100%"
              />
            </Box>
          </ModalBody>

          <ModalFooter>
            {canUpload && (
              <input type="file" accept={allowedFileTypes} onChange={handleFileChange} multiple={isMultiple} />
            )}
            <Spacer />
            <HStack>
              {canUpload && (
                <Button colorScheme="green" onClick={() => handleUploadFile()}>
                  Upload New Version
                </Button>
              )}
              <Button colorScheme="gray" onClick={closeModal}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

export default DocumentDetailModal
